import { useState } from "react";
import { Check, FlaskConical, Store, X } from "lucide-react";
import { useDemo } from "../context/DemoContext";
import { useAuth } from "../context/AuthContext";
import { BookmakersReal } from "../real/RealScreens";
import { MAX_BOOKMAKERS } from "@tracker-bet-analystic/domain";
import { EmptyState } from "../components/EmptyState";

export function Bookmakers() {
  const { bookmakers, addBookmaker, updateBookmaker, deleteBookmaker, isDemo } = useDemo();
  const { configured } = useAuth();
  const realMode = configured && !isDemo;
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [newName, setNewName] = useState("");
  const [error, setError] = useState("");

  const activeBookmakers = bookmakers.filter((b) => b.isActive).length;

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const name = newName.trim();
    if (!name) {
      setError("Nome da casa é obrigatório");
      return;
    }
    if (activeBookmakers >= MAX_BOOKMAKERS) {
      setError(`Limite de ${MAX_BOOKMAKERS} casas ativas atingido`);
      return;
    }

    const nextSlot = Math.max(0, ...bookmakers.map((b) => b.slot)) + 1;
    addBookmaker({ name, slot: nextSlot, isActive: true });
    setNewName("");
  };

  const handleEdit = (bookmaker: { id: string; name: string }) => {
    setEditingId(bookmaker.id);
    setEditName(bookmaker.name);
  };

  const handleSave = (id: string) => {
    const name = editName.trim();
    if (!name) {
      setError("Nome não pode ser vazio");
      return;
    }
    updateBookmaker(id, { name: editName });
    setEditingId(null);
    setEditName("");
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditName("");
  };

  const handleToggle = (bookmaker: { id: string; isActive: boolean; slot: number }) => {
    if (!bookmaker.isActive && activeBookmakers >= MAX_BOOKMAKERS) {
      setError(`Limite de ${MAX_BOOKMAKERS} casas ativas atingido`);
      return;
    }
    updateBookmaker(bookmaker.id, { isActive: !bookmaker.isActive });
  };

  const handleDelete = (id: string) => {
    if (window.confirm("Tem certeza? Isso removerá a casa e suas odds associadas.")) {
      deleteBookmaker(id);
    }
  };

  if (realMode) {
    return <BookmakersReal />;
  }

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-[var(--color-text)]">Minhas Casas</h1>
      </header>

      <form onSubmit={handleAdd} className="card p-4 mb-6 flex flex-col sm:flex-row gap-2 sm:items-end" aria-label="Adicionar casa">
        <div className="sm:flex-1">
          <label htmlFor="new-bookmaker" className="label">Nome da casa</label>
          <input
            id="new-bookmaker"
            type="text"
            className="input"
            placeholder="Ex: Bet365, Sportingbet, Pinnacle..."
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            maxLength={50}
          />
        </div>
        <button type="submit" className="btn btn-primary" disabled={activeBookmakers >= MAX_BOOKMAKERS}>
          {activeBookmakers >= MAX_BOOKMAKERS ? `Limite (${MAX_BOOKMAKERS}) atingido` : "Adicionar"}
        </button>
        {error && <span className="text-sm text-[var(--color-danger)]" role="alert">{error}</span>}
      </form>

      {bookmakers.length > 0 ? (
        <div className="card overflow-hidden">
          <div className="table-container">
            <table className="table" role="table">
              <thead>
                <tr>
                  <th scope="col">Slot</th>
                  <th scope="col">Nome</th>
                  <th scope="col">Status</th>
                  <th scope="col">Criada em</th>
                  <th scope="col">Ações</th>
                </tr>
              </thead>
              <tbody>
                {bookmakers.map((bm) => (
                  <tr key={bm.id}>
                    <td className="font-mono font-medium">#{bm.slot}</td>
                    <td>
                      {editingId === bm.id ? (
                        <input
                          type="text"
                          className="input w-full"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleSave(bm.id);
                            if (e.key === "Escape") handleCancel();
                          }}
                        />
                      ) : (
                        <span className="font-medium">{bm.name}</span>
                      )}
                    </td>
                    <td>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={bm.isActive}
                          onChange={() => handleToggle(bm)}
                          className="w-4 h-4 text-[var(--color-primary)] border-[var(--color-border)] rounded"
                        />
                        <span className="text-sm">{bm.isActive ? "Ativa" : "Inativa"}</span>
                      </label>
                    </td>
                    <td className="text-sm text-[var(--color-text-muted)]">
                      {new Date(bm.createdAt).toLocaleDateString("pt-BR")}
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        {editingId === bm.id ? (
                          <>
                            <button onClick={() => handleSave(bm.id)} className="btn btn-sm btn-primary" aria-label="Salvar"><Check size={16} aria-hidden="true" /></button>
                            <button onClick={handleCancel} className="btn btn-sm btn-ghost" aria-label="Cancelar"><X size={16} aria-hidden="true" /></button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => handleEdit(bm)} className="btn btn-sm btn-ghost" aria-label={`Editar ${bm.name}`}>✎</button>
                            <button onClick={() => handleDelete(bm.id)} className="btn btn-sm btn-ghost text-[var(--color-danger)]" aria-label={`Excluir ${bm.name}`}>🗑️</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <EmptyState
          icon={<Store />}
          title="Nenhuma casa configurada"
          text="Adicione suas casas de apostas acima para começar a registrar odds."
        />
      )}

      <div className="mt-8 p-4 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg">
        <h3 className="font-semibold mb-2">Regras</h3>
        <ul className="text-sm text-[var(--color-text-muted)] space-y-1">
          <li>• Máximo de {MAX_BOOKMAKERS} casas ativas simultaneamente</li>
          <li>• Nomes são livres — não há lista predefinida de casas</li>
          <li>• Casas inativas não aparecem no formulário de odds</li>
          <li>• O slot determina a ordem de exibição</li>
          <li>• Excluir uma casa remove suas odds dos relatórios futuros (snapshots antigos preservam o nome original)</li>
        </ul>
      </div>

      {isDemo && (
        <div className="mt-4 p-4 bg-[var(--color-demo-light)] border border-[var(--color-demo)] rounded-lg">
          <p className="text-sm text-[var(--color-demo)]">
            <FlaskConical size={14} aria-hidden="true" /> <strong>Modo Demonstração:</strong> As casas são armazenadas apenas na sessão atual.
            Recarregar a página restaura as casas demo iniciais.
          </p>
        </div>
      )}
    </div>
  );
}
